/**
 * SystemConsoleMenu — dropdown in the NDS header that surfaces every
 * internal admin surface. Anything not represented by a Capability tile
 * lives here so nothing gets orphaned when the OS shell is the primary
 * landing page.
 *
 *   • Click the pill → dropdown lists every admin route with an icon,
 *     label and one-line description.
 *   • Escape / click-outside closes it.
 *   • Non-blocking — floats over the header, no scrim.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Settings2, Receipt, GraduationCap, Tag, PlayCircle,
  KeyRound, Users, Layers, PencilRuler, Inbox, Link as LinkIcon,
  Workflow, ChevronDown, LineChart, Database, Building2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

/** Case-insensitive super-admin allowlist. Must mirror `SUPER_ADMIN_EMAILS`
 *  in backend/roi.py — the API enforces the same gate server-side, so a
 *  regular partner who guesses `/admin/roi` still hits a 403. This is a
 *  UX hint, not the security boundary. */
const SUPER_ADMIN_EMAILS = new Set(["will.s@nexengines.com", "william@nexengines.com"]);

const ITEMS = [
  { to: "/admin/roi",       Icon: LineChart,     label: "Credit ROI Dashboard", desc: "Super Admin · ECU burn + profit tracker", superAdminOnly: true },
  { to: "/admin/db",        Icon: Database,      label: "Database Manager",     desc: "Archive + hard delete rows across every collection", superAdminOnly: true },
  { to: "/admin/billing",   Icon: Receipt,       label: "Credits & Billing",    desc: "Burn rate · topup ledger · daily usage" },
  { to: "/admin/briefs",    Icon: Inbox,         label: "Briefs Inbox",         desc: "Every friction audit brief · triage" },
  { to: "/admin/users",     Icon: Users,         label: "Users & Roles",        desc: "Invite staff · assign permissions" },
  { to: "/admin/previews",  Icon: LinkIcon,      label: "Client Micro-Landings",desc: "Preview slots + access codes" },
  { to: "/admin/demos",     Icon: Building2,     label: "Demo Directory",       desc: "Business demos & 5-digit access codes" },
  { to: "/admin/tutorials", Icon: GraduationCap, label: "Tutorials Manager",    desc: "Onboarding walk-throughs" },
  { to: "/admin/sandbox",   Icon: PlayCircle,    label: "Presenter Sandbox",    desc: "Demo mode for pitches" },
  { to: "/admin/nds",       Icon: Layers,        label: "Schema Editor",        desc: "Full NDS-1 JSON editor" },
  { to: "/admin/layout",    Icon: Workflow,      label: "Layout Rules",         desc: "Section visibility + workflow logic" },
  { to: "/admin/labels",    Icon: Tag,           label: "Labels Editor",        desc: "Every UI string in one place" },
  { to: "/admin/settings",  Icon: KeyRound,      label: "PIN & Security",       desc: "Set your admin PIN" },
];

export default function SystemConsoleMenu() {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Read the current user's email from AuthContext so we can hide
  // Super-Admin-only items from other partners.
  const { user } = useAuth();
  const email = (user?.email || "").trim().toLowerCase();
  const isSuperAdmin = SUPER_ADMIN_EMAILS.has(email);
  const items = useMemo(
    () => ITEMS.filter((it) => !it.superAdminOnly || isSuperAdmin),
    [isSuperAdmin],
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="nds-system-console-toggle"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:border-nex-gold/50 text-nex-chrome hover:text-nex-gold font-mono text-[0.7rem] tracking-[0.14em] md:tracking-[0.22em] uppercase transition-all duration-200"
      >
        <Settings2 className="w-3.5 h-3.5" /> System
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="nds-system-console-menu"
          className="absolute right-0 top-full mt-2 w-[340px] bg-[#0B1220] border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.65)] z-50"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <div className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-gold uppercase">
              // System Console
            </div>
            <div className="text-nex-silver font-semibold text-sm mt-0.5">All internal ops surfaces</div>
          </div>
          <ul className="max-h-[70vh] overflow-y-auto py-1">
            {items.map(({ to, Icon, label, desc, superAdminOnly }) => (
              <li key={to}>
                <Link
                  to={to}
                  onClick={() => setOpen(false)}
                  data-testid={`nds-system-item-${to.replace(/\W+/g, "-")}`}
                  className="flex items-start gap-3 px-4 py-2.5 hover:bg-white/5 no-underline text-nex-silver transition-colors"
                >
                  <div className={`w-8 h-8 grid place-items-center border shrink-0 ${superAdminOnly ? "border-red-500/30 bg-red-500/[0.05] text-red-400" : "border-white/10 bg-white/[0.03] text-nex-gold"}`}>
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-nex-silver font-medium text-sm leading-tight flex items-center gap-1.5">
                      {label}
                      {superAdminOnly && <span className="font-mono text-[0.5rem] tracking-[0.16em] uppercase text-red-400/80">Super</span>}
                    </div>
                    <div className="text-[0.7rem] text-nex-muted leading-tight mt-0.5 truncate">{desc}</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
